import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getProfile, updateProfile } from "../../services/profile";
import { toast } from "react-toastify";
import logoImage from "../../assets/images/logo.png";
import CountryCodeSelect from "../common/CountryCodeSelect";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');

  .profile-root {
    font-family: 'Sora', sans-serif;
    background: #f7f5f2;
    min-height: 100%;
    padding: 28px 24px;
    color: #1a1a1a;
  }

  .profile-header-card {
    background: #fff;
    border-radius: 20px;
    padding: 24px;
    display: flex;
    align-items: center;
    gap: 18px;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    border: 1px solid #ede9e3;
  }

  .avatar-wrapper {
    position: relative;
    width: 76px;
    height: 76px;
    flex-shrink: 0;
    cursor: pointer;
  }

  .avatar-wrapper img {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #f0ece6;
    display: block;
    transition: filter 0.2s;
  }

  .avatar-wrapper:hover img {
    filter: brightness(0.82);
  }

  .avatar-overlay {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0,0,0,0.0);
    transition: background 0.2s;
    pointer-events: none;
  }

  .avatar-wrapper:hover .avatar-overlay {
    background: rgba(0,0,0,0.18);
  }

  .avatar-edit-badge {
    position: absolute;
    bottom: 2px;
    right: 2px;
    background: #E67E42;
    border: 2.5px solid #fff;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(230,126,66,0.35);
    transition: transform 0.15s;
  }

  .avatar-wrapper:hover .avatar-edit-badge {
    transform: scale(1.12);
  }

  .avatar-edit-badge svg {
    width: 11px;
    height: 11px;
    fill: #fff;
  }

  .avatar-upload-overlay {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(230,126,66,0.82);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .avatar-upload-spinner {
    width: 26px;
    height: 26px;
    border: 3px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .profile-header-info {
    flex: 1;
    min-width: 0;
  }

  .profile-header-name {
    font-size: 17px;
    font-weight: 700;
    color: #1a1a1a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .profile-header-email {
    font-size: 12.5px;
    color: #9b8e82;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .profile-orders-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 6px;
    background: #fdf3eb;
    border: 1px solid #f5d9c4;
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 11.5px;
    font-weight: 600;
    color: #E67E42;
  }

  .profile-form-card {
    background: #fff;
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    border: 1px solid #ede9e3;
  }

  .form-section-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .save-btn {
    background: #E67E42;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 9px 22px;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Sora', sans-serif;
    cursor: pointer;
    transition: background 0.18s, transform 0.1s, box-shadow 0.18s;
    box-shadow: 0 3px 10px rgba(230,126,66,0.28);
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .save-btn:hover:not(:disabled) {
    background: #d06a30;
    box-shadow: 0 4px 14px rgba(230,126,66,0.38);
  }

  .save-btn:active:not(:disabled) {
    transform: scale(0.97);
  }

  .save-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .save-btn-spinner {
    width: 13px;
    height: 13px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-field.full-width {
    grid-column: 1 / -1;
  }

  .form-label {
    font-size: 11.5px;
    font-weight: 600;
    color: #7a6e65;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .form-input-wrap {
    position: relative;
  }

  .form-input-wrap .field-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #c4b8ae;
    pointer-events: none;
    display: flex;
    align-items: center;
  }

  .field-input {
    width: 100%;
    background: #faf9f7;
    border: 1.5px solid #e8e1d9;
    border-radius: 10px;
    padding: 10px 12px 10px 36px;
    font-size: 13.5px;
    font-family: 'Sora', sans-serif;
    color: #1a1a1a;
    transition: border-color 0.18s, box-shadow 0.18s;
    box-sizing: border-box;
    outline: none;
    -webkit-appearance: none;
  }

  .field-input:focus {
    border-color: #E67E42;
    box-shadow: 0 0 0 3px rgba(230,126,66,0.12);
    background: #fff;
  }

  .field-input:disabled {
    background: #f0ece8;
    color: #a09590;
    cursor: not-allowed;
  }

  .field-input.no-icon {
    padding-left: 12px;
  }

  .field-select {
    width: 100%;
    background: #faf9f7;
    border: 1.5px solid #e8e1d9;
    border-radius: 10px;
    padding: 10px 12px 10px 36px;
    font-size: 13.5px;
    font-family: 'Sora', sans-serif;
    color: #1a1a1a;
    transition: border-color 0.18s, box-shadow 0.18s;
    box-sizing: border-box;
    outline: none;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23a09590' stroke-width='1.8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
  }

  .field-select:focus {
    border-color: #E67E42;
    box-shadow: 0 0 0 3px rgba(230,126,66,0.12);
    background-color: #fff;
  }

  .divider {
    border: none;
    border-top: 1px solid #ede9e3;
    margin: 20px 0;
  }

  @media (max-width: 520px) {
    .form-grid { grid-template-columns: 1fr; }
    .form-field.full-width { grid-column: 1; }
    .profile-root { padding: 16px 12px; }
  }
`;

// Simple SVG icons
const Icon = {
  User: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  ),
  Mail: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  Phone: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.5 19.79 19.79 0 0 1 1.65 2.72 2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16h.27z"/>
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  Globe: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  Gender: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="11" r="4"/><path d="M12 15v6M9 18h6"/>
    </svg>
  ),
  Pencil: () => (
    <svg viewBox="0 0 24 24" fill="white">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.46a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
    </svg>
  ),
  Orders: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  ),
};

export default function ProfileTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [profileData, setProfileData] = useState({
    full_name: "",
    gender: "",
    email: "",
    phone: "",
    birthdate: "",
    country_code: "",
    profile_image: null,
    total_orders: 0,
  });

  // Holds a newly selected File object (only set when user picks a new image)
  const [newImageFile, setNewImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await getProfile();
      const profile = response?.data || response;

      setProfileData({
        full_name: profile?.full_name || "",
        gender: profile?.gender || "",
        email: profile?.email || user?.email || "",
        phone: profile?.phone || "",
        birthdate: profile?.birthdate || "",
        country_code: profile?.country_code || "",
        profile_image: profile?.profile_image || null,
        total_orders: profile?.total_orders || 0,
      });

      setProfileImagePreview(
        profile?.profile_image && String(profile.profile_image).trim() !== ""
          ? profile.profile_image
          : null
      );

      // Clear any pending new image after a fresh fetch
      setNewImageFile(null);
    } catch (err) {
      console.error(err);
      toast.error("Error loading profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const compressImage = (file, maxWidth = 800, quality = 0.8) =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Compression failed"))),
          "image/jpeg",
          quality
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected if needed
    e.target.value = "";

    try {
      setUploadingImage(true);
      const compressedBlob = await compressImage(file);
      const compressedFile = new File([compressedBlob], file.name, { type: "image/jpeg" });

      // Show preview immediately
      setProfileImagePreview(URL.createObjectURL(compressedFile));
      setNewImageFile(compressedFile);

      // Upload right away
      await updateProfile({ ...profileData, profile_image: compressedFile });
      await fetchProfile();
      toast.success("Profile photo updated!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update profile photo.");
      // Revert preview on failure
      setProfileImagePreview(profileData.profile_image || null);
      setNewImageFile(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Pass newImageFile (File object or null). Service skips null images correctly.
      await updateProfile({ ...profileData, profile_image: newImageFile });
      await fetchProfile();
      toast.success("Profile updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Error updating profile.");
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const displayImage = profileImagePreview || profileData.profile_image || logoImage;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="profile-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 220 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 32, height: 32, border: "3px solid #e8e1d9", borderTopColor: "#E67E42", borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 10px" }} />
            <div style={{ color: "#9b8e82", fontSize: 13 }}>Loading profile…</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="profile-root">

        {/* ── Header Card ── */}
        <div className="profile-header-card">
          <div
            className="avatar-wrapper"
            onClick={() => !uploadingImage && fileInputRef.current?.click()}
            title="Change photo"
          >
            <img
              src={displayImage}
              alt="Profile"
              onError={(e) => (e.target.src = logoImage)}
            />
            {uploadingImage ? (
              <div className="avatar-upload-overlay">
                <div className="avatar-upload-spinner" />
              </div>
            ) : (
              <>
                <div className="avatar-overlay" />
                <div className="avatar-edit-badge">
                  <Icon.Pencil />
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
          </div>

          <div className="profile-header-info">
            <div className="profile-header-name">{profileData.full_name || "Your Name"}</div>
            <div className="profile-header-email">{profileData.email}</div>
            <div className="profile-orders-pill">
              <Icon.Orders />
              {profileData.total_orders} orders
            </div>
          </div>
        </div>

        {/* ── Form Card ── */}
        <div className="profile-form-card">
          <div className="form-section-title">
            <span>Personal Details</span>
            <button className="save-btn" onClick={handleSave} disabled={saving || uploadingImage}>
              {saving ? (
                <><div className="save-btn-spinner" /> Saving…</>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>

          <div className="form-grid">
            {/* Full Name */}
            <div className="form-field">
              <label className="form-label">Full Name</label>
              <div className="form-input-wrap">
                <span className="field-icon"><Icon.User /></span>
                <input
                  className="field-input"
                  value={profileData.full_name}
                  onChange={(e) => handleInputChange("full_name", e.target.value)}
                  placeholder="Enter your name"
                />
              </div>
            </div>

            {/* Gender */}
            <div className="form-field">
              <label className="form-label">Gender</label>
              <div className="form-input-wrap">
                <span className="field-icon"><Icon.Gender /></span>
                <select
                  className="field-select"
                  value={profileData.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Email */}
            <div className="form-field full-width">
              <label className="form-label">Email Address</label>
              <div className="form-input-wrap">
                <span className="field-icon"><Icon.Mail /></span>
                <input
                  className="field-input"
                  value={profileData.email}
                  disabled
                  placeholder="Email"
                />
              </div>
            </div>

            <hr className="divider" style={{ gridColumn: "1/-1", margin: "4px 0" }} />

            {/* Country Code */}
            <div className="form-field full-width">
              <label className="form-label">Country Code</label>
              <div className="form-input-wrap">
                <span className="field-icon"><Icon.Globe /></span>
                <CountryCodeSelect
                  value={profileData.country_code}
                  onChange={(value) => handleInputChange("country_code", value)}
                  placeholder="Select country code"
                  // Pass className so CountryCodeSelect uses the same styling
                  // (wrap it if needed — see note below)
                />
              </div>
            </div>

            {/* Phone */}
            <div className="form-field full-width">
              <label className="form-label">Phone Number</label>
              <div className="form-input-wrap">
                <span className="field-icon"><Icon.Phone /></span>
                <input
                  className="field-input"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="Enter phone number"
                  type="tel"
                />
              </div>
            </div>

            {/* Birthday */}
            <div className="form-field full-width">
              <label className="form-label">Date of Birth</label>
              <div className="form-input-wrap">
                <span className="field-icon"><Icon.Calendar /></span>
                <input
                  className="field-input"
                  type="date"
                  value={formatDateForInput(profileData.birthdate)}
                  onChange={(e) => handleInputChange("birthdate", e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}